import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StandardModal, { ModalSize } from '../../Fundamentals/Modal/StandardModal';
import ComboBox, { POSITIONING_STRATEGY } from '../../Fundamentals/ComboBox';
import Spinner, { SpinnerSize } from '../../Fundamentals/Spinner/Spinner';
import SpirisIcon from '../../Fundamentals/Icon/SpirisIcon';
import usePlanFormat from './usePlanFormat';
import type { PackagePlan, PackageSearchHit, PlanCopySource } from './Packages.types';

interface CopyPlanModalProps {
    packages: PackageSearchHit[];
    currentPackageId: string;
    loadPlans: (vossPackage: PackageSearchHit) => Promise<PackagePlan[] | null>;
    onPick: (source: PlanCopySource) => void;
}

const CopyPlanModal = ({ packages, currentPackageId, loadPlans, onPick }: CopyPlanModalProps) => {
    const { t } = useTranslation();
    const { formatCycleLength } = usePlanFormat();
    const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
    const [plans, setPlans] = useState<PackagePlan[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const selectedPackage = packages.find((vossPackage) => vossPackage.id === selectedPackageId);
    const packageOptions = useMemo(
        () =>
            packages
                .filter(
                    (vossPackage): vossPackage is PackageSearchHit & { id: string } =>
                        vossPackage.id !== undefined && vossPackage.id.toLowerCase() !== currentPackageId.toLowerCase()
                )
                .map((vossPackage) => ({ id: vossPackage.id, name: vossPackage.name ?? vossPackage.id }))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [packages, currentPackageId]
    );

    useEffect(() => {
        if (selectedPackage === undefined) {
            setPlans(null);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setPlans(null);
        // a failed request resolves null and has already toasted; leaving plans null keeps the "no plans" text for real empties only
        void loadPlans(selectedPackage).then((result) => {
            if (cancelled) return;
            setPlans(result);
            setIsLoading(false);
        });

        // a slower response for a previously picked package must not overwrite the plans of the current one
        return () => {
            cancelled = true;
        };
    }, [selectedPackage, loadPlans]);

    return (
        <StandardModal title={t('packages.copyPlanTitle')} size={ModalSize.LARGE}>
            <p className="text-secondary font-13">{t('packages.copyPlanDescription')}</p>
            <ComboBox
                label={t('common.package')}
                placeholder={t('packages.selectPackage') ?? ''}
                options={packageOptions}
                value={selectedPackageId}
                valueKey="id"
                labelKey="name"
                strategy={POSITIONING_STRATEGY.DIALOG}
                onChange={(value) => setSelectedPackageId(typeof value === 'string' ? value : null)}
            />
            {isLoading && <Spinner size={SpinnerSize.SMALL} />}
            {plans !== null && plans.length === 0 && (
                <p className="my-16">
                    <i>{t('packages.copyPlanNoPlans')}</i>
                </p>
            )}
            {plans !== null && plans.length > 0 && selectedPackage !== undefined && (
                <div className="list-group mt-16" role="list">
                    {plans.map((plan) => {
                        const externalId = plan.externalId ?? '';

                        return (
                            <button
                                key={plan.id}
                                type="button"
                                role="listitem"
                                className="list-group-item d-flex align-items-center gap-16 w-100 text-left cursorpointer"
                                onClick={() => onPick({ plan, packageName: selectedPackage.name ?? '' })}
                            >
                                <SpirisIcon name="credit-card" />
                                <span className="flex-1 min-w-0">
                                    <span className="font-weight-600 d-block">{plan.name}</span>
                                    {externalId.length > 0 && <span className="text-secondary package-plan-mono d-block">{externalId}</span>}
                                    <span className="d-flex flex-wrap gap-6 mt-4">
                                        <span className="label">
                                            {t('packages.bindingCycle')}: {formatCycleLength(plan.bindingPeriodCycle?.length)}
                                        </span>
                                        <span className="label">
                                            {t('packages.subscriptionBilling')}: {formatCycleLength(plan.subscriptionBillingPeriodCycle?.length)}
                                        </span>
                                        <span className="label">
                                            {t('packages.usageBilling')}: {formatCycleLength(plan.usageBillingPeriodCycle?.length)}
                                        </span>
                                    </span>
                                </span>
                                <SpirisIcon name="chevron-right" size="sm" />
                            </button>
                        );
                    })}
                </div>
            )}
        </StandardModal>
    );
};

export default CopyPlanModal;
